// What solving a room means.
//
// Until now (2026-08-13) nothing in the world said this. A room was a tilemap,
// some doors and some objects, and "solved" existed only in a player's head and
// in the shape of a Playwright spec -- which is why every question of the form
// "how many solutions does this have", "is there a shorter one", "can this be
// stranded" was unaskable of the data rather than merely expensive. This is the
// first stage of the Puzzle Analysis workspace (see `09_Roadmap.md`), and it is
// the one piece the rest is blocked on.
//
// **A goal is always just a tile.** That falls out of a fact about this
// simulation rather than being a simplification: nothing in `sim/` transitions
// rooms. The game does, from `GameScene`'s own `doorAt` check on the caster's
// step, and `replay.ts` says outright that nothing here walks through a door. A
// `DOOR` tile is not `WALL`, `VOID` or a cliff, so `queries.tileIsFree` already
// lets the caster stand on one. So marking the exit *doorway* works headlessly
// with no new machinery at all: walking into it leaves the caster standing in
// it, which is exactly the reading "solved" wants.
//
// The other half of the rule is presence, not walkability, and it is what makes
// room10 work without a special case -- see `isRoomSolved`.
import { ObjectDef, RoomDef } from './types';
import { CasterTile } from './queries';

/** One authored goal marker, resolved to the tile it stands on.
 *
 * `objectId` is set for an object marker and absent for a door, because those
 * are the two genuinely different completion tests: a door is a place, and an
 * object is a place *plus* a thing that has to be there. */
export interface GoalMarker {
  kind: 'object' | 'door';
  /** The marked object's id, for `kind: 'object'` only. */
  objectId?: string;
  x: number;
  y: number;
}

/** Every goal marker in a room, doors first, then objects in authored order.
 *
 * Nested `activatesInto` defs are walked, the same way `mechanismCensus` walks
 * them: a dormant object that will exist later is a real object, and marking one
 * is the whole point of the nested case. A nested marker reports the nested
 * def's own tile and id -- which is where it will be and what it will be called
 * once the swap happens.
 *
 * Returns a list rather than the single marker `goalTile` wants, because "how
 * many are there" is the question the loader has to ask, and answering it by
 * finding one and stopping would be answering a different one. */
export function goalMarkers(room: RoomDef): GoalMarker[] {
  const markers: GoalMarker[] = [];
  for (const door of room.doors ?? []) {
    if (door.goal) markers.push({ kind: 'door', x: door.x, y: door.y });
  }
  const walk = (obj: ObjectDef): void => {
    if (obj.goal) markers.push({ kind: 'object', objectId: obj.id, x: obj.x, y: obj.y });
    if (obj.activatesInto) walk(obj.activatesInto);
  };
  for (const obj of room.objects ?? []) walk(obj);
  return markers;
}

/** The room's one goal marker, or null.
 *
 * Null covers two different situations that deliberately read the same here: a
 * room that has no goal because nobody has authored one yet, and a room that has
 * no goal because it is not a puzzle (room11's overworld bay, room16's Proving
 * Floor). The analysis workspace distinguishes them by looking at `room.puzzle`;
 * the simulation has no reason to.
 *
 * A second marker is a load error, so by the time anything calls this there is
 * at most one -- but this is deliberately still total rather than throwing on a
 * room that never went through `loadRoom` (the editor holds live rooms, and a
 * half-authored one is a normal state to be in). It takes the first. */
export function goalMarker(room: RoomDef): GoalMarker | null {
  return goalMarkers(room)[0] ?? null;
}

/** Where the goal is, or null if this room has none. */
export function goalTile(room: RoomDef): { x: number; y: number } | null {
  const marker = goalMarker(room);
  return marker ? { x: marker.x, y: marker.y } : null;
}

/** Whether the caster has solved this room.
 *
 * Takes a room and a tile rather than a `WorldState`, following `queries.ts`
 * beside it -- and here that is load-bearing rather than stylistic. `load.ts`
 * calls `goalMarkers` above, and `state.ts` reaches `load.ts` through
 * `rooms/index.ts`, so importing the world here would close a module cycle
 * (`load -> goal -> state -> rooms -> load`). `world.player` structurally
 * satisfies `CasterTile`, so every real caller still writes one line.
 *
 * **The caster is on the goal tile, and (for an object goal) the goal object is
 * present.** Two clauses, and the second one is doing more work than it looks.
 *
 * Presence rather than a walkability re-check, deliberately. Re-deriving "is
 * this tile walkable" would be asking a question the caster's own position has
 * already answered -- they could not be standing there otherwise -- and it would
 * get the awkward case wrong in both directions (a gate that shuts on top of
 * them; a barrier reforming under their feet). What presence buys instead is
 * room10, the one room in the world whose completion is not an exit: its Portal
 * does not exist until four altars are filled, arriving via `activatesInto` when
 * `checkRuneAltars` swaps it in for the Rune Circle. Mark the dormant Portal and
 * the goal is unsatisfiable until that happens -- the caster can stand on the
 * Circle's tile from the first move and it means nothing, because the object
 * named is not there yet. No special case, no altar-counting here, and nothing
 * in this file knows what an altar is.
 *
 * It also means a goal object that has been destroyed takes the goal with it,
 * which is the honest reading: there is nothing left to reach.
 *
 * **A door goal additionally requires its lock to be open**, and that clause was
 * missing until a search went looking for solutions and found one (2026-08-13):
 * a ten-move walk straight onto room1's east door, which needs the Rusty Key.
 * The reason it is needed is a divergence between the simulation and the game,
 * and it is worth being precise about because this file's own header leans on
 * the other half of it. Headlessly, nothing stops the caster standing on a
 * `DOOR` tile -- which is what makes a door goal expressible at all. In the
 * game they never do: `GameScene` checks `doorAt` *before* moving, and either
 * the door is locked, in which case the lock panel opens and the caster does not
 * move, or it is open, in which case `enterRoom` carries them to the next room.
 * Standing on a doorway is a state the real game cannot produce.
 *
 * So the tile is the sim's stand-in for going through, and it is only a solution
 * when going through is something the caster could actually do. `doorLocks`
 * defaults to empty, and empty means locked (see `state.ts`'s `doorLocks`: an
 * absent entry is a locked door), so a caller that forgets it gets the
 * conservative answer rather than a free pass. */
export function isRoomSolved(room: RoomDef, caster: CasterTile, doorLocks: Record<string, boolean> = {}): boolean {
  const marker = goalMarker(room);
  if (!marker) return false;
  if (caster.x !== marker.x || caster.y !== marker.y) return false;
  if (marker.kind === 'object') return room.objects.some((o) => o.id === marker.objectId);
  const door = room.doors.find((d) => d.x === marker.x && d.y === marker.y);
  return !door?.lockId || isDoorUnlocked(doorLocks, door.lockId);
}

/** Whether a lock has been opened.
 *
 * A named predicate for a one-line lookup, because **the polarity of
 * `doorLocks` is the opposite of what its name suggests and has already been
 * got wrong once** (2026-08-13, in the first version of `isRoomSolved` above,
 * where it was written as `=== true` and the test agreed with it, so both were
 * wrong together). The stored boolean is *is this locked*, exactly as
 * `game/src/doors.ts`'s `isDoorLocked` reads it -- `locks()[lockId] ?? true` --
 * so an absent entry is a locked door and `false` is the value that means open.
 * Unlocking is `setDoorLocked(id, false)`.
 *
 * The sim had no predicate of its own, which is why the mistake was available
 * to make: the polarity had to be inferred from a comment rather than read off
 * a function. Now it is read off a function. */
export function isDoorUnlocked(doorLocks: Record<string, boolean>, lockId: string): boolean {
  return doorLocks[lockId] === false;
}
